import { formatMoneyAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { abbreviateNumber } from '@src/utils/data';
import { Header } from '@Components/StickyTable/BPMTable';
import { TableProgressBar } from '@Components/TableProgressBar';
import { AmcMediaMixTableRow } from './amc-media-mix-types';

const GRAND_TOTAL_MEDIA_TYPE = 'Grand Total';

type RateField = 'purchaseRate' | 'avgOrderSize' | 'reach' | 'purchased';

const formatValue = (value: number, field: RateField): string => {
  if (field === 'purchaseRate') return `${abbreviateNumber(value)}%`;
  if (field === 'avgOrderSize') return formatMoneyAsInt(value);
  return String(abbreviateNumber(value));
};

// The Grand Total row shows a plain formatted value; every other row shows a progress bar
// relative to the column's max — bpm-frontend special-cased this by mediaType rather than
// routing the total through BPMTable's own `totals` prop (kept as-is here for parity).
const progressBarRenderer = (
  value: number,
  maxCompleted: number,
  field: RateField,
  mediaType: string
) => {
  const formatted = formatValue(value, field);
  if (mediaType === GRAND_TOTAL_MEDIA_TYPE) return <div>{formatted}</div>;
  return <TableProgressBar label={formatted} maxCompleted={maxCompleted} value={value} />;
};

export interface MediaMixMaxValues {
  reach: number;
  proPurchased: number;
  proPurchaseRate: number;
  proOrderSize: number;
  ntbPurchased: number;
  ntbPurchaseRate: number;
  ntbOrderSize: number;
}

const mediaTypeHeader: Header = {
  label: 'Media Type',
  name: 'mediaType',
  flex: 1,
  minFlexWidth: 150,
  renderer: (data: AmcMediaMixTableRow) => data.mediaType
};

export const getProspectiveTableHeaders = (maxValues: MediaMixMaxValues): Header[] => [
  mediaTypeHeader,
  {
    label: 'Shoppers Reached',
    name: 'reach',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(Number(data.reach), maxValues.reach, 'reach', data.mediaType)
  },
  {
    label: 'Prospective Purchased',
    name: 'purchased',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.purchased),
        maxValues.proPurchased,
        'purchased',
        data.mediaType
      )
  },
  {
    label: 'Prospective Purchase Rate',
    name: 'purchaseRate',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.purchaseRate),
        maxValues.proPurchaseRate,
        'purchaseRate',
        data.mediaType
      )
  },
  {
    label: 'Avg Order Value',
    name: 'avgOrderSize',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.avgOrderSize),
        maxValues.proOrderSize,
        'avgOrderSize',
        data.mediaType
      )
  }
];

export const getNtbTableHeaders = (maxValues: MediaMixMaxValues): Header[] => [
  mediaTypeHeader,
  {
    label: 'Shoppers Reached',
    name: 'reach',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(Number(data.reach), maxValues.reach, 'reach', data.mediaType)
  },
  {
    label: 'NTB Purchased',
    name: 'purchased',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.purchased),
        maxValues.ntbPurchased,
        'purchased',
        data.mediaType
      )
  },
  {
    label: 'NTB Purchase Rate',
    name: 'purchaseRate',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.purchaseRate),
        maxValues.ntbPurchaseRate,
        'purchaseRate',
        data.mediaType
      )
  },
  {
    label: 'Avg Order Value',
    name: 'avgOrderSize',
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: AmcMediaMixTableRow) =>
      progressBarRenderer(
        Number(data.avgOrderSize),
        maxValues.ntbOrderSize,
        'avgOrderSize',
        data.mediaType
      )
  }
];
